import type { TreeOption } from 'naive-ui';
import {
    createAssemblyMateProjectionDisplayContext,
    isAssemblyMateDiagnosticError,
    normalizeAssemblyNodeLookupId,
    normalizeOptionalAssemblyNodeId,
    projectAssemblyMatesByAssembly,
    projectAssemblyMatesByComponent,
} from '@puppycad/cad-core';
import type {
    AssemblyMateDocumentModel,
    AssemblyMateProjectionDisplayContext,
    AssemblyMateProjectionNode,
} from '@puppycad/cad-core';
import {
    FeatureTreeDataSource,
    FeatureTreeNodeKind,
} from './feature-tree-types';
import type { FeatureTreeNodeMeta } from './feature-tree-types';

/**
 * 装配体配合设计树投影结果。
 */
export interface AssemblyMateTreeProjectionResult {
    /** 装配体节点 ID 到配合文件夹树节点的映射。 */
    readonly folderByAssemblyNodeId: ReadonlyMap<string, TreeOption>;
    /** 组件或子装配体节点 ID 到 Mate in assembly 文件夹树节点的映射。 */
    readonly componentFolderByComponentNodeId: ReadonlyMap<string, TreeOption>;
    /** 配合节点元数据映射。 */
    readonly featureTreeNodeMap: Map<string, FeatureTreeNodeMeta>;
}

/**
 * 装配节点匹配到的配合文件夹。
 */
export interface ResolvedAssemblyMateProjectedFolder {
    /** cad-core 投影中的所属装配节点 ID。 */
    readonly assemblyNodeId: string;
    /** 配合文件夹树节点。 */
    readonly folder: TreeOption;
}

/**
 * 组件或子装配体节点匹配到的 Mate in assembly 文件夹。
 */
export interface ResolvedAssemblyMateProjectedComponentFolder {
    /** cad-core 投影中的参与组件节点 ID。 */
    readonly componentNodeId: string;
    /** Mate in assembly 文件夹树节点。 */
    readonly folder: TreeOption;
}

/**
 * 规范化装配节点模糊匹配 ID。
 *
 * @param value 原始装配节点 ID。
 * @returns 去除实例索引后的路径 ID。
 */
export function normalizeAssemblyMateLookupId(value: string): string {
    return normalizeAssemblyNodeLookupId(value);
}

/**
 * 创建配合文件夹树节点 key。
 *
 * @param assemblyNodeId 所属装配体节点 ID。
 * @returns 树节点 key。
 */
export function createAssemblyMateFolderTreeKey(assemblyNodeId: string): string {
    return `assembly-mates-folder:${assemblyNodeId}`;
}

/**
 * 创建空配合文件夹节点。
 *
 * @param assemblyNodeId 所属装配体节点 ID。
 * @param featureTreeNodeMap 节点元数据映射。
 * @returns 空配合文件夹节点。
 */
export function buildEmptyAssemblyMateFolderTreeNode(
    assemblyNodeId: string,
    featureTreeNodeMap: Map<string, FeatureTreeNodeMeta>,
): TreeOption {
    const key = createAssemblyMateFolderTreeKey(assemblyNodeId);
    featureTreeNodeMap.set(key, {
        kind: FeatureTreeNodeKind.AssemblyMateFolder,
        source: FeatureTreeDataSource.State,
        objectId: assemblyNodeId,
        objectType: 'assembly-mate-folder',
        assemblyMateOwnerAssemblyNodeId: assemblyNodeId,
    });

    return {
        key,
        label: '配合',
        type: 'assembly-mate-folder',
        iconType: 'assembly-mate-folder',
        isLeaf: true,
        children: [],
    };
}

/**
 * 按设计树装配节点候选 ID 解析 Mate 文件夹。
 *
 * 设计说明：
 * GLB 设计树节点与 imported-model 选择快照在不同层级可能使用不同 ID 形式：
 * 有些路径带 `Assembly1[0]` 这类实例索引，有些树节点只保留 `Assembly1`。
 * 这里先做精确匹配，再做去实例索引后的模糊匹配，保证已提交 Mate 不会因为显示层 ID
 * 差异从设计树中丢失。
 *
 * @param projection Mate 树投影。
 * @param assemblyNodeIds 设计树装配节点候选 ID。
 * @param claimedAssemblyNodeIds 已被其它树节点消费的投影装配节点 ID。
 * @returns 匹配到的配合文件夹。
 */
export function resolveAssemblyMateProjectedFolder(
    projection: AssemblyMateTreeProjectionResult,
    assemblyNodeIds: readonly string[],
    claimedAssemblyNodeIds: ReadonlySet<string>,
): ResolvedAssemblyMateProjectedFolder | null {
    for (const assemblyNodeId of assemblyNodeIds) {
        const folder = projection.folderByAssemblyNodeId.get(assemblyNodeId);
        if (folder && !claimedAssemblyNodeIds.has(assemblyNodeId)) {
            return {
                assemblyNodeId,
                folder,
            };
        }
    }

    const lookupIds = new Set(assemblyNodeIds.map(normalizeAssemblyMateLookupId));
    const fuzzyMatches: ResolvedAssemblyMateProjectedFolder[] = [];
    for (const [projectedAssemblyNodeId, folder] of projection.folderByAssemblyNodeId.entries()) {
        if (claimedAssemblyNodeIds.has(projectedAssemblyNodeId)) {
            continue;
        }
        if (!lookupIds.has(normalizeAssemblyMateLookupId(projectedAssemblyNodeId))) {
            continue;
        }
        fuzzyMatches.push({
            assemblyNodeId: projectedAssemblyNodeId,
            folder,
        });
    }

    return fuzzyMatches.length === 1 ? fuzzyMatches[0] ?? null : null;
}

/**
 * 按设计树组件节点候选 ID 解析 Mate in assembly 文件夹。
 *
 * @param projection Mate 树投影。
 * @param componentNodeIds 设计树组件节点候选 ID。
 * @param claimedComponentNodeIds 已被其它树节点消费的组件节点 ID。
 * @returns 匹配到的 Mate in assembly 文件夹。
 */
export function resolveAssemblyMateProjectedComponentFolder(
    projection: AssemblyMateTreeProjectionResult,
    componentNodeIds: readonly string[],
    claimedComponentNodeIds: ReadonlySet<string>,
): ResolvedAssemblyMateProjectedComponentFolder | null {
    for (const componentNodeId of componentNodeIds) {
        const folder = projection.componentFolderByComponentNodeId.get(componentNodeId);
        if (folder && !claimedComponentNodeIds.has(componentNodeId)) {
            return {
                componentNodeId,
                folder,
            };
        }
    }

    const lookupIds = new Set(componentNodeIds.map(normalizeAssemblyMateLookupId));
    const fuzzyMatches: ResolvedAssemblyMateProjectedComponentFolder[] = [];
    for (const [projectedComponentNodeId, folder] of projection.componentFolderByComponentNodeId.entries()) {
        if (claimedComponentNodeIds.has(projectedComponentNodeId)) {
            continue;
        }
        if (!lookupIds.has(normalizeAssemblyMateLookupId(projectedComponentNodeId))) {
            continue;
        }
        fuzzyMatches.push({
            componentNodeId: projectedComponentNodeId,
            folder,
        });
    }

    return fuzzyMatches.length === 1 ? fuzzyMatches[0] ?? null : null;
}

/**
 * 收集尚未挂到任何装配树节点的 Mate 文件夹。
 *
 * @param projection Mate 树投影。
 * @param claimedAssemblyNodeIds 已消费的投影装配节点 ID。
 * @returns 未消费的配合文件夹。
 */
export function collectUnclaimedAssemblyMateFolders(
    projection: AssemblyMateTreeProjectionResult,
    claimedAssemblyNodeIds: ReadonlySet<string>,
): readonly ResolvedAssemblyMateProjectedFolder[] {
    return [...projection.folderByAssemblyNodeId.entries()]
        .filter(([assemblyNodeId]) => !claimedAssemblyNodeIds.has(assemblyNodeId))
        .map(([assemblyNodeId, folder]) => ({
            assemblyNodeId,
            folder,
        }));
}

/**
 * 规范化设计树节点 ID。
 *
 * @param value 原始节点 ID。
 * @returns 可用于匹配 cad-core Mate 投影的 ID。
 */
function normalizeAssemblyMateNodeId(value: unknown): string {
    return normalizeOptionalAssemblyNodeId(value);
}

/**
 * 判断设计树节点是否为装配体或子装配体节点。
 *
 * @param meta 设计树节点元数据。
 * @returns 是否为装配体节点。
 */
function isAssemblyTreeNode(meta: FeatureTreeNodeMeta | undefined): boolean {
    return meta?.kind === FeatureTreeNodeKind.GlbNode && meta.glbNodeRole === 'assembly';
}

/**
 * 判断设计树节点是否可承载 Mate in assembly 文件夹。
 *
 * @param meta 设计树节点元数据。
 * @returns 是否为组件或子装配体节点。
 */
function isAssemblyComponentTreeNode(meta: FeatureTreeNodeMeta | undefined): boolean {
    return meta?.kind === FeatureTreeNodeKind.GlbNode
        && (meta.glbNodeRole === 'part' || meta.glbNodeRole === 'assembly');
}

/**
 * 从当前权威设计树元数据构建 cad-core Mate 显示上下文。
 *
 * 设计说明：
 * 1. web-client 只提供 imported-model 已解析的名称和实例序号，不拼接 Mate 文案；
 * 2. 固定状态、配置和显示状态只存在于 `displayName`，不能作为配合参与者名称来源；
 * 3. 编码/解码节点 ID 的别名登记由 cad-core 统一完成。
 *
 * @param nodeMap 当前设计树节点元数据。
 * @returns cad-core Mate 投影显示上下文。
 */
export function createAssemblyMateProjectionDisplayContextFromNodeMap(
    nodeMap: ReadonlyMap<string, FeatureTreeNodeMeta> | null | undefined,
): AssemblyMateProjectionDisplayContext {
    const entries = [...(nodeMap?.values() ?? [])]
        .filter((meta) => meta.kind === FeatureTreeNodeKind.GlbNode)
        .map((meta) => ({
            assemblyNodeId: meta.assemblyNodeId ?? meta.glbAssemblyNodeId ?? '',
            displayName: meta.assemblyNodeDisplayName ?? meta.glbExternalAssemblyName ?? '',
            instanceIndex: meta.assemblyNodeInstanceIndex,
        }))
        .filter((entry) => entry.assemblyNodeId.trim().length > 0 && entry.displayName.trim().length > 0);

    return createAssemblyMateProjectionDisplayContext(entries);
}

/**
 * 解析设计树 GLB 节点可用于匹配 Mate 投影的候选 ID。
 *
 * @param node 设计树节点。
 * @param meta 设计树节点元数据。
 * @returns 候选节点 ID。
 */
function resolveAssemblyMateNodeIdCandidates(
    node: TreeOption,
    meta: FeatureTreeNodeMeta,
): string[] {
    const candidates = [
        // 装配配合归属必须优先使用稳定装配节点 ID；显示 key/objectId 只用于兼容旧 GLB 树。
        meta.assemblyNodeId,
        meta.glbAssemblyNodeId,
        meta.glbPath,
        meta.glbSourceObjectId,
        meta.objectId,
        typeof node.key === 'string' ? node.key : undefined,
    ]
        .map(normalizeAssemblyMateNodeId)
        .filter((item) => item.length > 0);

    return [...new Set(candidates)];
}

/**
 * 解析装配节点下应该显示的配合文件夹。
 *
 * @param assemblyNodeIds 装配节点候选 ID。
 * @param projection Mate 树投影。
 * @param targetNodeMap 设计树节点元数据映射。
 * @param claimedAssemblyNodeIds 已消费的装配节点 ID。
 * @returns 配合文件夹树节点。
 */
function resolveAssemblyMateFolderForAssemblyNode(
    assemblyNodeIds: readonly string[],
    projection: AssemblyMateTreeProjectionResult,
    targetNodeMap: Map<string, FeatureTreeNodeMeta>,
    claimedAssemblyNodeIds: ReadonlySet<string>,
): TreeOption | null {
    const projectedFolder = resolveAssemblyMateProjectedFolder(
        projection,
        assemblyNodeIds,
        claimedAssemblyNodeIds,
    );
    if (projectedFolder) {
        return projectedFolder.folder;
    }

    const fallbackAssemblyNodeId = assemblyNodeIds[0] ?? '';
    if (!fallbackAssemblyNodeId) {
        return null;
    }

    return buildEmptyAssemblyMateFolderTreeNode(fallbackAssemblyNodeId, targetNodeMap);
}

/**
 * 收集顶层装配体设计树节点 key。
 *
 * @param treeData 原始设计树。
 * @param targetNodeMap 设计树节点元数据映射。
 * @returns 顶层装配体节点 key 集合。
 */
function collectTopLevelAssemblyTreeNodeKeys(
    treeData: readonly TreeOption[],
    targetNodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>,
): ReadonlySet<string> {
    const result = new Set<string>();
    const visit = (node: TreeOption, hasAssemblyAncestor: boolean): void => {
        const key = typeof node.key === 'string' ? node.key : String(node.key ?? '');
        const meta = targetNodeMap.get(key);
        const currentIsAssembly = isAssemblyTreeNode(meta);
        if (currentIsAssembly && !hasAssemblyAncestor) {
            result.add(key);
        }
        const children = Array.isArray(node.children) ? node.children as TreeOption[] : [];
        children.forEach((child) => visit(child, hasAssemblyAncestor || currentIsAssembly));
    };

    treeData.forEach((node) => visit(node, false));
    return result;
}

/**
 * 合并配合文件夹子节点。
 *
 * @param baseFolder 当前装配节点的配合文件夹。
 * @param extraFolders 需要兜底挂载的配合文件夹。
 * @returns 合并后的配合文件夹。
 */
function mergeAssemblyMateFolderChildren(
    baseFolder: TreeOption,
    extraFolders: readonly TreeOption[],
): TreeOption {
    if (extraFolders.length === 0) {
        return baseFolder;
    }

    const existingChildren = Array.isArray(baseFolder.children)
        ? baseFolder.children as TreeOption[]
        : [];
    const extraChildren = extraFolders.flatMap((folder) => (
        Array.isArray(folder.children) ? folder.children as TreeOption[] : []
    ));
    const children = [...existingChildren, ...extraChildren];

    return {
        ...baseFolder,
        children,
        isLeaf: children.length === 0,
        statusError: Boolean(baseFolder.statusError) || extraFolders.some((folder) => Boolean(folder.statusError)),
    };
}

/**
 * 将 cad-core Mate 投影注入设计树。
 *
 * 设计说明：
 * 1. 装配体 / 子装配体节点始终拥有自己的“配合”文件夹；
 * 2. 参与 Mate 的组件或子装配体额外挂载 SOLIDWORKS 风格的 “Mate in assembly” 文件夹；
 * 3. 本函数只按稳定 ID 纯数据合并树，不读取 Object3D、userData 或 Vue 响应式对象。
 *
 * @param treeData 原始设计树。
 * @param projection Mate 树投影。
 * @param targetNodeMap 设计树节点元数据映射。
 * @returns 合并后的设计树。
 */
export function mergeAssemblyMateFoldersIntoTree(
    treeData: readonly TreeOption[],
    projection: AssemblyMateTreeProjectionResult,
    targetNodeMap: Map<string, FeatureTreeNodeMeta>,
): TreeOption[] {
    const topLevelAssemblyNodeKeys = collectTopLevelAssemblyTreeNodeKeys(treeData, targetNodeMap);
    const claimedAssemblyNodeIds = new Set<string>();
    const claimedComponentNodeIds = new Set<string>();

    const mergeNode = (node: TreeOption): TreeOption => {
        const key = typeof node.key === 'string' ? node.key : String(node.key ?? '');
        const meta = targetNodeMap.get(key);
        const childNodes: TreeOption[] = Array.isArray(node.children)
            ? (node.children as TreeOption[]).map(mergeNode)
            : [];
        const childrenWithoutMateFolder = childNodes.filter((child) => child.type !== 'assembly-mate-folder');
        const nextMateFolders: TreeOption[] = [];

        if (meta && isAssemblyTreeNode(meta)) {
            const assemblyNodeIds = resolveAssemblyMateNodeIdCandidates(node, meta);
            const projectedFolder = resolveAssemblyMateProjectedFolder(
                projection,
                assemblyNodeIds,
                claimedAssemblyNodeIds,
            );
            if (projectedFolder) {
                claimedAssemblyNodeIds.add(projectedFolder.assemblyNodeId);
            }
            const folderNode = projectedFolder?.folder
                ?? resolveAssemblyMateFolderForAssemblyNode(
                    assemblyNodeIds,
                    projection,
                    targetNodeMap,
                    claimedAssemblyNodeIds,
                );
            const unclaimedFolders = topLevelAssemblyNodeKeys.has(key)
                ? collectUnclaimedAssemblyMateFolders(projection, claimedAssemblyNodeIds)
                : [];
            unclaimedFolders.forEach((item) => {
                claimedAssemblyNodeIds.add(item.assemblyNodeId);
            });
            if (folderNode) {
                nextMateFolders.push(mergeAssemblyMateFolderChildren(
                    folderNode,
                    unclaimedFolders.map((item) => item.folder),
                ));
            }
        }

        if (meta && isAssemblyComponentTreeNode(meta)) {
            const componentNodeIds = resolveAssemblyMateNodeIdCandidates(node, meta);
            const projectedComponentFolder = resolveAssemblyMateProjectedComponentFolder(
                projection,
                componentNodeIds,
                claimedComponentNodeIds,
            );
            if (projectedComponentFolder) {
                claimedComponentNodeIds.add(projectedComponentFolder.componentNodeId);
                nextMateFolders.push(projectedComponentFolder.folder);
            }
        }

        const nextChildren = [...childrenWithoutMateFolder, ...nextMateFolders];
        return {
            ...node,
            children: nextChildren,
            isLeaf: nextChildren.length === 0,
        };
    };

    return treeData.map(mergeNode);
}

/**
 * 解析 Mate 树节点图标类型。
 *
 * @param node cad-core 投影节点。
 * @returns 设计树图标类型。
 */
function resolveAssemblyMateTreeIconType(node: AssemblyMateProjectionNode): string {
    if (node.kind === 'folder') {
        return 'assembly-mate-folder';
    }

    /**
     * SOLIDWORKS 会用锁图标明确提示同轴心配合已锁定旋转。
     * 该状态必须来自 cad-core 的 Mate Definition 投影，UI 只做图标适配。
     */
    if (node.lockRotation === true) {
        return 'assembly-mate-lock-rotation';
    }

    return 'assembly-mate';
}

/**
 * 将 cad-core Mate 投影节点转换为 NaiveUI 树节点。
 *
 * @param node cad-core 投影节点。
 * @param featureTreeNodeMap 节点元数据映射。
 * @returns NaiveUI 树节点。
 */
function buildAssemblyMateTreeNode(
    node: AssemblyMateProjectionNode,
    featureTreeNodeMap: Map<string, FeatureTreeNodeMeta>,
): TreeOption {
    const children = node.children?.map((child) => buildAssemblyMateTreeNode(child, featureTreeNodeMap)) ?? [];
    const isFolder = node.kind === 'folder';

    featureTreeNodeMap.set(node.id, {
        kind: isFolder ? FeatureTreeNodeKind.AssemblyMateFolder : FeatureTreeNodeKind.AssemblyMate,
        source: FeatureTreeDataSource.State,
        objectId: isFolder
            ? node.ownerComponentNodeId ?? node.ownerAssemblyNodeId
            : node.mateId,
        objectType: isFolder ? 'assembly-mate-folder' : 'assembly-mate',
        displayName: node.label,
        mateId: node.mateId,
        mateType: node.mateType,
        mateSuppressed: !isFolder ? node.suppressed === true : undefined,
        mateSolveStatus: !isFolder ? node.solveStatus : undefined,
        mateErrorMessage: !isFolder ? node.errorMessage : undefined,
        mateParticipants: !isFolder ? node.participants : undefined,
        assemblyMateOwnerAssemblyNodeId: node.ownerAssemblyNodeId,
        assemblyMateOwnerComponentNodeId: node.ownerComponentNodeId,
    });

    return {
        key: node.id,
        label: node.label,
        type: isFolder ? 'assembly-mate-folder' : 'assembly-mate',
        iconType: resolveAssemblyMateTreeIconType(node),
        isLeaf: !isFolder || children.length === 0,
        statusSuppressed: !isFolder && node.suppressed === true,
        statusError: isAssemblyMateDiagnosticError(node.solveStatus, node.errorMessage),
        // 完整投影和增量更新共享响应式诊断字段，避免同状态错误文本更新时提示不刷新。
        mateSolveStatus: !isFolder ? node.solveStatus : undefined,
        mateErrorMessage: !isFolder ? node.errorMessage : undefined,
        mateParticipants: !isFolder ? node.participants : undefined,
        children,
    };
}

/**
 * 构建装配体 Mate 在设计树中的节点投影。
 *
 * 设计说明：
 * 1. cad-core 负责 Mate 文档与 SOLIDWORKS 语义投影，web-client 只把投影适配为树组件需要的数据；
 * 2. 配合文件夹是否显示由设计树装配节点注入逻辑决定，当前函数只负责已有 Mate 的归属投影；
 * 3. 节点只保留 mateId、状态和显示文案，不反查 Object3D 或保存运行时引用。
 *
 * @param document Mate 文档。
 * @param sourceNodeMap 当前 imported-model 设计树元数据，用于提供权威名称与实例序号。
 * @param cachedDisplayContext 同一模型结构/名称版本内复用的 SDK 显示上下文；完整投影可省略。
 * @returns 设计树投影。
 */
export function buildAssemblyMateTreeProjection(
    document: AssemblyMateDocumentModel | null | undefined,
    sourceNodeMap?: ReadonlyMap<string, FeatureTreeNodeMeta> | null,
    cachedDisplayContext?: AssemblyMateProjectionDisplayContext,
): AssemblyMateTreeProjectionResult {
    const featureTreeNodeMap = new Map<string, FeatureTreeNodeMeta>();
    if (!document) {
        return {
            folderByAssemblyNodeId: new Map(),
            componentFolderByComponentNodeId: new Map(),
            featureTreeNodeMap,
        };
    }

    const displayContext = cachedDisplayContext ?? createAssemblyMateProjectionDisplayContextFromNodeMap(sourceNodeMap);
    const folderByAssemblyNodeId = new Map<string, TreeOption>();
    projectAssemblyMatesByAssembly(document, displayContext).forEach((projection) => {
        folderByAssemblyNodeId.set(
            projection.assemblyNodeId,
            buildAssemblyMateTreeNode(projection.folder, featureTreeNodeMap),
        );
    });
    const componentFolderByComponentNodeId = new Map<string, TreeOption>();
    projectAssemblyMatesByComponent(document, displayContext).forEach((projection) => {
        componentFolderByComponentNodeId.set(
            projection.componentNodeId,
            buildAssemblyMateTreeNode(projection.folder, featureTreeNodeMap),
        );
    });

    return {
        folderByAssemblyNodeId,
        componentFolderByComponentNodeId,
        featureTreeNodeMap,
    };
}
